import React, { useMemo, useState } from "react";
import { DatePicker, Input, Select, message } from "antd";
import moment, { Moment } from "moment";
import { EditOutlined } from "@ant-design/icons";
import { Button } from "../../../../design-system";
import { PageHeader } from "../components/PageHeader";
import { StatusTag } from "../components/StatusTag";
import { useTailSpend } from "../TailSpendContext";
import { PolicyVersion } from "../model/types";
import classes from "./GuardrailsView.module.less";
import opsClasses from "./shared.module.less";

const { TextArea } = Input;

const AUTO_EXECUTE_OPTIONS = [
    "When terms are inside the envelope",
    "When terms are inside the envelope and benchmark confidence is high",
    "Require buyer confirmation before execution",
    "Disabled · always route to you",
];

// Parse "$150,000" → 150000, or null if not parseable.
const parseDollar = (s: string): number | null => {
    const digits = s.replace(/[^\d]/g, "");
    return digits ? parseInt(digits, 10) : null;
};

const formatDollar = (n: number | null): string =>
    n == null ? "" : `$${n.toLocaleString()}`;

// Parse "$X,000 – $Y,000" into [X, Y].
const parseBand = (s: string): [number | null, number | null] => {
    const parts = s.split(/[–-]/).map((p) => p.trim());
    return [parseDollar(parts[0] ?? ""), parseDollar(parts[1] ?? "")];
};

// Short plain-English explanation for each guardrail label, keyed by the
// label as it appears in the policy summary.
const EXPLANATIONS: Record<string, string> = {
    "Autonomous scope":
        "Largest deal the agent will close on its own. Anything bigger is automatically routed to your strategic team.",
    "Auto-execute":
        "When the supplier accepts terms inside the envelope, the agent issues the PR/PO and writes it back to the ERP with no human review.",
    "Buyer e-sign first":
        "Deals in this band are still negotiated by the agent, but you must countersign the order form before the requisition is created.",
    "Hand to strategic above":
        "Once the deal's value crosses this cap, the agent stops and hands the negotiation to your strategic sourcing team with the full playbook attached.",
    "Walk-away floor":
        "The hard limits the agent will not concede past. If the supplier's counter breaches any of these, the deal is paused and routed to you.",
    "Levers in play":
        "Variables the agent is allowed to negotiate. Anything else (legal, IP, scope) is treated as fixed and never touched.",
    "Required clauses":
        "Clauses that must appear in every closed deal. The agent confirms each one before binding.",
    "Concession ladder":
        "How many back-and-forth concessions the agent will make before escalating. Keeps a stalled negotiation from dragging on.",
};

const LEVER_OPTIONS = [
    "Unit price",
    "Payment terms",
    "Volume rebate",
    "Auto-renewal removal",
    "Fuel-surcharge cap",
    "Index pass-through cap",
    "Accessorials",
    "Lane rate",
    "Service rate",
];

const splitChips = (raw: string): string[] =>
    raw
        .split("·")
        .map((s) => s.trim())
        .filter(Boolean);

const joinChips = (chips: string[]): string => chips.join(" · ");

export const GuardrailsView: React.FC = () => {
    const { workspace } = useTailSpend();
    const policiesWithDetail = workspace.policies.filter(
        (p) => p.summary.length > 4
    );
    const [selectedId, setSelectedId] = useState(
        policiesWithDetail[0]?.id ?? workspace.policies[0]?.id
    );

    const policy = workspace.policies.find((p) => p.id === selectedId);
    const category = workspace.categories.find(
        (c) => c.policyId === policy?.id
    );

    const versions: PolicyVersion[] = useMemo(() => {
        if (policy?.versions && policy.versions.length > 0) return policy.versions;
        // Fall back: synthesize a single current version from the summary so
        // the selector always has something to show.
        return policy
            ? [
                  {
                      version: 1,
                      startDate: "—",
                      summary: policy.summary,
                  },
              ]
            : [];
    }, [policy]);

    const latestIdx = versions.length - 1;
    const [versionIdx, setVersionIdx] = useState(latestIdx);
    const activeVersion = versions[Math.min(versionIdx, latestIdx)];
    const isCurrent = activeVersion === versions[latestIdx];

    const initialForm = useMemo<Array<[string, string]>>(
        () => activeVersion?.summary.map(([k, v]) => [k, v] as [string, string]) ?? [],
        [activeVersion]
    );
    const [editing, setEditing] = useState(false);
    const [form, setForm] = useState<Array<[string, string]>>(initialForm);
    const [newStartDate, setNewStartDate] = useState<Moment | null>(
        moment().add(1, "day")
    );

    // Reset whenever the active version changes.
    React.useEffect(() => {
        setForm(initialForm);
        setEditing(false);
    }, [initialForm]);

    // Reset version cursor to the latest whenever the policy changes.
    React.useEffect(() => {
        setVersionIdx(versions.length - 1);
    }, [versions]);

    const openEdit = () => {
        setForm(initialForm);
        setNewStartDate(moment().add(1, "day"));
        setEditing(true);
    };

    const cancelEdit = () => {
        setEditing(false);
        setForm(initialForm);
    };

    const saveEdit = () => {
        setEditing(false);
        const dateLabel = newStartDate?.format("MMM D, YYYY") ?? "today";
        message.success({
            content: `${category?.name ?? "Policy"} guardrails updated. New version takes effect ${dateLabel}; the previous one will be archived.`,
            duration: 3,
        });
    };

    const updateRow = (index: number, value: string) => {
        setForm((rows) =>
            rows.map((row, i) => (i === index ? [row[0], value] : row))
        );
    };

    const renderEditor = (label: string, value: string, index: number) => {
        switch (label) {
            case "Autonomous scope":
            case "Hand to strategic above":
                return (
                    <Input
                        prefix="$"
                        value={value.replace(/^\$/, "")}
                        onChange={(e) =>
                            updateRow(
                                index,
                                e.target.value
                                    ? `$${e.target.value.replace(/^\$/, "")}`
                                    : ""
                            )
                        }
                        style={{ width: 200 }}
                    />
                );
            case "Buyer e-sign first": {
                const [min, max] = parseBand(value);
                const onMin = (raw: string) => {
                    const n = parseDollar(raw);
                    updateRow(index, `${formatDollar(n)} – ${formatDollar(max)}`);
                };
                const onMax = (raw: string) => {
                    const n = parseDollar(raw);
                    updateRow(index, `${formatDollar(min)} – ${formatDollar(n)}`);
                };
                return (
                    <div className={classes.bandRow}>
                        <Input
                            prefix="$"
                            value={min == null ? "" : min.toLocaleString()}
                            onChange={(e) => onMin(e.target.value)}
                            style={{ width: 180 }}
                        />
                        <span className={classes.bandSep}>to</span>
                        <Input
                            prefix="$"
                            value={max == null ? "" : max.toLocaleString()}
                            onChange={(e) => onMax(e.target.value)}
                            style={{ width: 180 }}
                        />
                    </div>
                );
            }
            case "Auto-execute":
                return (
                    <Select
                        value={value}
                        onChange={(nv) => updateRow(index, nv)}
                        style={{ width: "100%" }}
                        options={AUTO_EXECUTE_OPTIONS.map((o) => ({
                            value: o,
                            label: o,
                        }))}
                    />
                );
            case "Concession ladder":
                return (
                    <Select
                        value={value}
                        onChange={(nv) => updateRow(index, nv)}
                        style={{ width: 320 }}
                        options={[
                            { value: "Up to 1 reciprocal concession before escalation", label: "Up to 1 reciprocal concession" },
                            { value: "Up to 2 reciprocal concessions before escalation", label: "Up to 2 reciprocal concessions" },
                            { value: "Up to 3 reciprocal concessions before escalation", label: "Up to 3 reciprocal concessions" },
                        ]}
                    />
                );
            case "Levers in play":
                return (
                    <Select
                        mode="multiple"
                        allowClear
                        value={splitChips(value)}
                        onChange={(chips: string[]) => updateRow(index, joinChips(chips))}
                        style={{ width: "100%" }}
                        placeholder="Pick levers the agent can negotiate"
                        options={LEVER_OPTIONS.map((l) => ({ value: l, label: l }))}
                    />
                );
            case "Required clauses":
                return (
                    <Select
                        mode="tags"
                        allowClear
                        value={splitChips(value)}
                        onChange={(chips: string[]) => updateRow(index, joinChips(chips))}
                        style={{ width: "100%" }}
                        placeholder="Add a clause and press Enter"
                        tokenSeparators={["·"]}
                    />
                );
            case "Walk-away floor":
                return (
                    <TextArea
                        value={value}
                        onChange={(e) => updateRow(index, e.target.value)}
                        autoSize={{ minRows: 2, maxRows: 4 }}
                    />
                );
            default:
                return (
                    <Input
                        value={value}
                        onChange={(e) => updateRow(index, e.target.value)}
                    />
                );
        }
    };

    return (
        <>
            <PageHeader
                title="Guardrails"
                subtitle="Set once per category. These limits define what the agents may do without a human present, and exactly where they must stop and escalate to you."
            />

            <div className={opsClasses.toolbar}>
                <div className={classes.pickers}>
                    <div className={classes.picker}>
                        <label className={classes.pickerLabel}>Category</label>
                        <Select
                            value={selectedId}
                            onChange={(v) => setSelectedId(v as typeof selectedId)}
                            style={{ minWidth: 260 }}
                            options={policiesWithDetail.map((p) => ({
                                label:
                                    workspace.categories.find(
                                        (c) => c.policyId === p.id
                                    )?.name ?? "",
                                value: p.id,
                            }))}
                        />
                    </div>
                    <div className={classes.picker}>
                        <label className={classes.pickerLabel}>Version</label>
                        <Select
                            value={versionIdx}
                            onChange={(v) => setVersionIdx(v)}
                            style={{ minWidth: 280 }}
                            options={versions.map((vs, i) => {
                                const isLatest = i === versions.length - 1;
                                const range = isLatest
                                    ? `${vs.startDate} → current`
                                    : `${vs.startDate} → ${vs.endDate}`;
                                return {
                                    value: i,
                                    label: `v${vs.version} · ${range}`,
                                };
                            })}
                        />
                    </div>
                </div>
                <div className={classes.actions}>
                    {isCurrent ? (
                        <StatusTag tone="green">Active</StatusTag>
                    ) : (
                        <StatusTag tone="slate">Archived · read-only</StatusTag>
                    )}
                    {isCurrent && !editing && (
                        <Button icon={<EditOutlined />} onClick={openEdit}>
                            Edit policy
                        </Button>
                    )}
                    {isCurrent && editing && (
                        <>
                            <Button onClick={cancelEdit}>Cancel</Button>
                            <Button type="primary" onClick={saveEdit}>
                                Save policy
                            </Button>
                        </>
                    )}
                </div>
            </div>

            <div className={classes.policyCard} data-tour-id="guardrailsPage">
                {editing && (
                    <div className={classes.editStart}>
                        <label className={classes.startLabel}>
                            New version takes effect on
                        </label>
                        <DatePicker
                            value={newStartDate}
                            onChange={(d) => setNewStartDate(d)}
                            format="MMM D, YYYY"
                            allowClear={false}
                        />
                    </div>
                )}
                {form.map(([k, v], i) => (
                    <div key={k} className={classes.policyRow}>
                        <div className={classes.k}>{k}</div>
                        <div className={classes.body}>
                            {editing ? (
                                renderEditor(k, v, i)
                            ) : (
                                <div className={classes.v}>{v}</div>
                            )}
                            {EXPLANATIONS[k] && (
                                <div className={classes.explain}>
                                    {EXPLANATIONS[k]}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
};
